import { useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

// ─── NAV DATA (shared by desktop sidebar + mobile drawer + bottom nav) ──────

const navItems = [
  { section: "OVERVIEW",  items: [{ icon: "ti-layout-dashboard", label: "Dashboard",     path: "/dashboard"     }] },
  { section: "ENGAGE",    items: [
    { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
    { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
    { icon: "ti-users",        label: "Leads",         path: "/leads"         },
  ]},
  { section: "CONFIGURE", items: [
    { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
    { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
    { icon: "ti-settings", label: "Settings",       path: "/settings"  },
  ]},
];

// ─── DESKTOP SIDEBAR (unchanged look, now hidden on mobile) ──────────────────

function Sidebar() {
  const navigate = useNavigate();
  return (
    <aside className="hidden lg:flex flex-col h-full border-r flex-shrink-0"
      style={{ width: "15%", minWidth: 180, background: "#161412", borderColor: "#2a2825" }}
    >
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-base" />
        </div>
        <div>
          <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
          <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
        </div>
      </div>

      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
        {navItems.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
            {items.map(({ icon, label, path }) => {
              const active = path === "/settings";
              return (
                <div
                  key={label}
                  onClick={() => navigate(path)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 transition-all ${
                    active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                          : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
                  }`}
                >
                  <i className={`ti ${icon} text-base flex-shrink-0`} />
                  <span className="text-xs font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
          <i className="ti ti-shield-check text-orange-700" />
          Secured workspace
        </div>
        <div className="text-[9px] text-stone-700">
          © 2026{" "}
          <a
            href="https://awktechz.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-orange-500 transition-colors"
          >
            AWK Technologies
          </a>
        </div>
      </div>
    </aside>
  );
}

// ─── MOBILE DRAWER MENU ──────────────────────────────────────────────────────

function MobileDrawer({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 lg:hidden" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose} />
      <div className="fixed top-0 left-0 bottom-0 z-50 flex flex-col lg:hidden"
        style={{ width: 260, background: "#161412", borderRight: "1px solid #2a2825" }}>
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
              <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500" style={{ background: "#252320" }}>
            <i className="ti ti-x text-sm" />
          </button>
        </div>

        <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
          {navItems.map(({ section, items }) => (
            <div key={section}>
              <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
              {items.map(({ icon, label, path }) => {
                const active = path === "/settings";
                return (
                  <div
                    key={label}
                    onClick={() => { navigate(path); onClose(); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer mb-0.5 transition-all ${
                      active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                             : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
                    }`}
                  >
                    <i className={`ti ${icon} text-base flex-shrink-0`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-5 pb-6 border-t pt-4" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
            <i className="ti ti-shield-check text-orange-700" />
            Secured workspace
          </div>
          <div className="text-[9px] text-stone-700">
            © 2026{" "}
            <a
              href="https://awktechz.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors"
            >
              AWK Technologies
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MOBILE BOTTOM NAV ───────────────────────────────────────────────────────

function MobileBottomNav() {
  const navigate = useNavigate();
  const items = [
    { icon: "ti-layout-dashboard", label: "Home",  path: "/dashboard"     },
    { icon: "ti-robot",            label: "VOX",   path: "/ai-agent"      },
    { icon: "ti-message-dots",     label: "Chats", path: "/conversations" },
    { icon: "ti-users",            label: "Leads", path: "/leads"         },
    { icon: "ti-book",             label: "KB",    path: "/knowledge"     },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around border-t lg:hidden"
      style={{ background: "#161412", borderColor: "#2a2825", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {items.map(({ icon, label, path }) => {
        const active = path === "/settings";
        return (
          <button key={path} onClick={() => navigate(path)}
            className="flex flex-col items-center gap-0.5 py-2.5 px-3 flex-1 transition-all"
            style={{ color: active ? "#e8602a" : "#6b6560" }}>
            <i className={`ti ${icon} text-lg`} />
            <span style={{ fontSize: 9, fontWeight: active ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── TOPBAR ───────────────────────────────────────────────────────────────────
function Topbar({ onLogout, onMenuOpen }) {
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);
  
  return (
    <header className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b flex-shrink-0"
      style={{ background: "#161412", borderColor: "#2a2825" }}
    >
      {/* Hamburger — mobile only */}
      <button onClick={onMenuOpen}
        className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 flex-shrink-0"
        style={{ background: "#242220" }}>
        <i className="ti ti-menu-2 text-base" />
      </button>

      <div className="hidden lg:flex gap-1.5">
        {["ti-moon-stars", "ti-sun"].map((ic) => (
          <button key={ic}
            className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-300 transition-colors"
            style={{ background: "#242220" }}
          >
            <i className={`ti ${ic} text-xs`} />
          </button>
        ))}
      </div>

      <div className="flex-1 flex items-center gap-2 rounded-full px-3 lg:px-4 py-2 border lg:mx-4"
        style={{ background: "#1e1c1a", borderColor: "#333" }}
      >
        <i className="ti ti-search text-stone-600 text-xs" />
        <span className="text-[11px] text-stone-600 hidden sm:block">Search settings…</span>
        <span className="text-[11px] text-stone-600 sm:hidden">Search…</span>
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* Logout button with dropdown */}
        <div className="relative">
          <button
            onMouseEnter={() => setShowLogoutDropdown(true)}
            onMouseLeave={() => setShowLogoutDropdown(false)}
            onClick={onLogout}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-orange-700 transition-all duration-200 shadow-lg"
            style={{ background: "#e8602a" }}
            title="Logout"
          >
            <i className="ti ti-power text-lg" />
          </button>

          {/* Dropdown on hover */}
          {showLogoutDropdown && (
            <div
              onMouseEnter={() => setShowLogoutDropdown(true)}
              onMouseLeave={() => setShowLogoutDropdown(false)}
              className="absolute right-0 top-full mt-1 w-32 rounded-xl border shadow-2xl py-1 z-50"
              style={{ background: "#1a1815", borderColor: "#2a2825" }}
            >
              <button
                onClick={onLogout}
                className="w-full px-4 py-2.5 text-left text-sm text-stone-300 hover:bg-orange-500/10 hover:text-orange-400 transition-colors flex items-center gap-2"
              >
                <i className="ti ti-logout text-orange-500" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
// ─── REUSABLE FIELD COMPONENTS ────────────────────────────────────────────────
function SectionCard({ icon, iconBg, title, subtitle, children }) {
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ background: "#1a1815", borderColor: "#2a2825" }}>
      <div className="flex items-center gap-3 px-4 lg:px-6 py-4 border-b" style={{ borderColor: "#2a2825", background: "#1e1c1a" }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
          <i className={`ti ${icon} text-sm text-white`} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-stone-100">{title}</div>
          {subtitle && <div className="text-[10px] text-stone-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="px-4 lg:px-6 py-5 flex flex-col gap-5">{children}</div>
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, placeholder, value, onChange, type = "text", hint, fullWidth = false }) {
  return (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? "sm:col-span-2" : ""}`}>
      <label className="text-[10px] text-stone-500 uppercase tracking-wider font-medium">{label}</label>
      {hint && <div className="text-[10px] text-stone-600 -mt-1">{hint}</div>}
      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border transition-colors focus-within:border-orange-600/50"
        style={{ background: "#252320", borderColor: "#333" }}
      >
        <input
          type={type}
          className="bg-transparent text-[12px] text-stone-200 placeholder-stone-600 outline-none flex-1"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function TextareaField({ label, placeholder, value, onChange, hint }) {
  return (
    <div className="flex flex-col gap-1.5 sm:col-span-2">
      <label className="text-[10px] text-stone-500 uppercase tracking-wider font-medium">{label}</label>
      {hint && <div className="text-[10px] text-stone-600 -mt-1">{hint}</div>}
      <textarea
        rows={3}
        className="rounded-xl px-3 py-2.5 border text-[12px] text-stone-200 placeholder-stone-600 outline-none resize-none transition-colors focus:border-orange-600/50"
        style={{ background: "#252320", borderColor: "#333" }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
function Toggle({ label, sub, checked, onChange, color = "#e8602a" }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl px-4 py-3 border"
      style={{ background: "#252320", borderColor: "#2a2825" }}
    >
      <div className="min-w-0">
        <div className="text-[12px] font-medium text-stone-200">{label}</div>
        {sub && <div className="text-[10px] text-stone-600 mt-0.5">{sub}</div>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className="relative w-10 h-5.5 rounded-full flex-shrink-0 transition-all"
        style={{ background: checked ? color : "#333", width: 40, height: 22 }}
      >
        <span
          className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
          style={{ left: checked ? "calc(100% - 18px)" : 2 }}
        />
      </button>
    </div>
  );
}

function SelectField({ label, value, onChange, options, hint }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] text-stone-500 uppercase tracking-wider font-medium">{label}</label>
      {hint && <div className="text-[10px] text-stone-600 -mt-1">{hint}</div>}
      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border transition-colors focus-within:border-orange-600/50"
        style={{ background: "#252320", borderColor: "#333" }}
      >
        <select
          className="bg-transparent text-[12px] text-stone-200 outline-none flex-1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => <option key={o} className="bg-stone-900">{o}</option>)}
        </select>
      </div>
    </div>
  );
}

function Divider() {
  return <div className="border-t" style={{ borderColor: "#2a2825" }} />;
}

// ─── SETTINGS SECTIONS ────────────────────────────────────────────────────────
const settingsTabs = [
  { id: "business",      icon: "ti-building",       label: "Business"    },
  { id: "ecosystem",     icon: "ti-topology-star",   label: "Ecosystem"   },
  { id: "ai",            icon: "ti-brain",            label: "AI"          },
  { id: "notifications", icon: "ti-bell",             label: "Notifications"},
  { id: "security",      icon: "ti-shield-lock",      label: "Security"    },
  { id: "billing",       icon: "ti-credit-card",      label: "Billing"     },
];
// ─── TAB: BUSINESS ────────────────────────────────────────────────────────────
function BusinessTab({ data, setData }) {
  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon="ti-building-store"
        iconBg="linear-gradient(135deg,#e8602a,#c84a1e)"
        title="Business Profile"
        subtitle="Your organisation's public-facing information"
      >
        <FieldRow>
          <Field label="Business Name" placeholder="Fitness Zone"
            value={data.name} onChange={(v) => setData({ ...data, name: v })} />
          <Field label="Contact Email" placeholder="sajid.ali@example.com" type="email"
            value={data.email} onChange={(v) => setData({ ...data, email: v })} />
        </FieldRow>
        <FieldRow>
          <Field label="Phone Number" placeholder="+1 415 000 0000"
            value={data.phone} onChange={(v) => setData({ ...data, phone: v })} />
          <Field label="Website" placeholder="https://awktechnologies.com"
            value={data.website} onChange={(v) => setData({ ...data, website: v })} />
        </FieldRow>
        <TextareaField label="Business Description"
          placeholder="Briefly describe what your business does…"
          hint="Used by the AI agent to contextualise responses."
          value={data.description} onChange={(v) => setData({ ...data, description: v })} />
        <Divider />
        <FieldRow>
          <SelectField label="Industry" value={data.industry}
            onChange={(v) => setData({ ...data, industry: v })}
            options={["Fitness & Wellness", "SaaS / Tech", "E-commerce", "Healthcare", "Finance", "Education", "Other"]} />
          <SelectField label="Timezone" value={data.timezone}
            onChange={(v) => setData({ ...data, timezone: v })}
            options={["UTC+5 (PKT)", "UTC+0 (GMT)", "UTC-5 (EST)", "UTC-8 (PST)", "UTC+1 (CET)", "UTC+3 (AST)"]} />
        </FieldRow>
      </SectionCard>
    </div>
  );
}
// ─── TAB: ECOSYSTEM ───────────────────────────────────────────────────────────
function EcosystemTab({ data, setData }) {
  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon="ti-topology-star"
        iconBg="linear-gradient(135deg,#a855f7,#7c3aed)"
        title="Nigella Ecosystem Bridge"
        subtitle="Push qualified leads straight into NigellaFit / Nigella ERP"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Toggle label="NigellaFit Customer"
            sub="Sync leads to NigellaFit CRM automatically"
            checked={data.nigellaFit}
            onChange={(v) => setData({ ...data, nigellaFit: v })}
            color="#e8602a" />
          <Toggle label="Nigella ERP Customer"
            sub="Push prospects into Nigella ERP pipeline"
            checked={data.nigellaERP}
            onChange={(v) => setData({ ...data, nigellaERP: v })}
            color="#a855f7" />
        </div>
        <Field label="Ecosystem Webhook URL" fullWidth
          placeholder="https://erp.nigella.app/api/prospects"
          hint="The endpoint that receives lead payloads from Nigella VOX."
          value={data.webhookUrl}
          onChange={(v) => setData({ ...data, webhookUrl: v })} />
        <FieldRow>
          <Field label="API Key / Token"
            placeholder="nig_live_••••••••••••••••"
            type="password"
            value={data.apiToken}
            onChange={(v) => setData({ ...data, apiToken: v })} />
          <SelectField label="Sync Trigger" value={data.syncTrigger}
            onChange={(v) => setData({ ...data, syncTrigger: v })}
            options={["On Lead Qualified", "On Conversation End", "Manual Only", "Real-time"]} />
        </FieldRow>
        <Divider />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl px-4 py-3 border"
          style={{ background: "#252320", borderColor: "#2a2825" }}
        >
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" style={{ boxShadow: "0 0 6px #34c97c" }} />
            <span className="text-[12px] text-stone-300 font-medium">Bridge is active</span>
            <span className="text-[10px] text-stone-600">· Last sync 3 min ago</span>
          </div>
          <button className="text-[10px] text-orange-500 font-medium hover:text-orange-400 transition-colors flex-shrink-0 self-start sm:self-auto">Test Connection</button>
        </div>
      </SectionCard>
    </div>
  );
}
// ─── TAB: AI ──────────────────────────────────────────────────────────────────
function AITab({ data, setData }) {
  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon="ti-brain"
        iconBg="linear-gradient(135deg,#5b8dee,#3a6ad4)"
        title="AI Configuration"
        subtitle="Optionally use your own OpenAI key (leave blank to use the platform key)"
      >
        <Field label="Custom OpenAI API Key"
          placeholder="sk-…"
          type="password"
          hint="Your key will be stored encrypted and never exposed in the UI."
          value={data.openAiKey}
          onChange={(v) => setData({ ...data, openAiKey: v })}
          fullWidth />
        <Divider />
        <FieldRow>
          <SelectField label="Default AI Model" value={data.model}
            onChange={(v) => setData({ ...data, model: v })}
            options={["Nigella VOX v1.3 (default)", "GPT-4o", "GPT-4 Turbo", "GPT-3.5 Turbo", "Claude 3.5 Sonnet"]} />
          <SelectField label="Response Language" value={data.language}
            onChange={(v) => setData({ ...data, language: v })}
            options={["English", "Urdu", "Arabic", "French", "Spanish", "German", "Auto-detect"]} />
        </FieldRow>
        <TextareaField label="Agent System Prompt"
          placeholder="You are a helpful AI assistant for {business_name}. Always be polite, concise, and professional…"
          hint="Custom instructions given to the AI agent at the start of every conversation."
          value={data.systemPrompt}
          onChange={(v) => setData({ ...data, systemPrompt: v })} />
        <Divider />
        <div className="flex flex-col gap-2">
          {[
            { key: "autoEscalate",  label: "Auto-Escalate to Human",   sub: "Hand off to a live agent when confidence is low" },
            { key: "leadScoring",   label: "AI Lead Scoring",           sub: "Automatically score and tag leads based on conversation" },
            { key: "sentimentAnalysis", label: "Sentiment Analysis",    sub: "Detect user sentiment and adjust tone accordingly" },
            { key: "multiLingual",  label: "Multi-language Detection",  sub: "Auto-detect and reply in the visitor's language" },
          ].map(({ key, label, sub }) => (
            <Toggle key={key} label={label} sub={sub}
              checked={data[key]}
              onChange={(v) => setData({ ...data, [key]: v })} />
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
// ─── TAB: NOTIFICATIONS ───────────────────────────────────────────────────────
function NotificationsTab({ data, setData }) {
  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon="ti-bell"
        iconBg="linear-gradient(135deg,#f59e0b,#d97706)"
        title="Notification Preferences"
        subtitle="Choose when and how you receive alerts from Nigella VOX"
      >
        <div className="flex flex-col gap-2">
          {[
            { key: "newLead",      label: "New Lead Captured",        sub: "Get notified when the AI qualifies a new lead" },
            { key: "escalation",   label: "Escalation Required",      sub: "Alert when a conversation needs human attention" },
            { key: "weeklyReport", label: "Weekly Summary Report",    sub: "Receive a digest of performance metrics every Monday" },
            { key: "channelDown",  label: "Channel Disconnected",     sub: "Alert if a connected channel goes offline" },
            { key: "lowScore",     label: "Low Lead Score Alert",     sub: "Notify when a lead scores below 40" },
          ].map(({ key, label, sub }) => (
            <Toggle key={key} label={label} sub={sub}
              checked={data[key]}
              onChange={(v) => setData({ ...data, [key]: v })}
              color="#f59e0b" />
          ))}
        </div>
        <Divider />
        <FieldRow>
          <Field label="Notification Email" placeholder="alerts@awktechnologies.com" type="email"
            value={data.notifEmail} onChange={(v) => setData({ ...data, notifEmail: v })} />
          <SelectField label="Alert Frequency" value={data.frequency}
            onChange={(v) => setData({ ...data, frequency: v })}
            options={["Instant", "Hourly Digest", "Daily Digest", "Weekly Only"]} />
        </FieldRow>
      </SectionCard>
    </div>
  );
}
// ─── TAB: SECURITY ────────────────────────────────────────────────────────────
function SecurityTab({ data, setData }) {
  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon="ti-shield-lock"
        iconBg="linear-gradient(135deg,#34c97c,#1fa85e)"
        title="Security & Access"
        subtitle="Manage authentication, sessions, and workspace access controls"
      >
        <FieldRow>
          <Field label="Current Password" placeholder="••••••••" type="password"
            value={data.currentPw} onChange={(v) => setData({ ...data, currentPw: v })} />
          <Field label="New Password" placeholder="••••••••" type="password"
            value={data.newPw} onChange={(v) => setData({ ...data, newPw: v })} />
        </FieldRow>
        <Divider />
        <div className="flex flex-col gap-2">
          {[
            { key: "twoFA",         label: "Two-Factor Authentication", sub: "Require OTP on every login" },
            { key: "sessionAlert",  label: "New Session Alerts",        sub: "Email me when a new device logs in" },
            { key: "ipWhitelist",   label: "IP Whitelisting",           sub: "Restrict console access to specific IP ranges" },
          ].map(({ key, label, sub }) => (
            <Toggle key={key} label={label} sub={sub}
              checked={data[key]}
              onChange={(v) => setData({ ...data, [key]: v })}
              color="#34c97c" />
          ))}
        </div>
        <Divider />
        <div>
          <div className="text-[10px] text-stone-500 uppercase tracking-wider mb-2">Active Sessions</div>
          <div className="flex flex-col gap-2">
            {[
              { device: "Chrome · Windows 11", location: "Jhelum, PK", time: "Now", current: true },
              { device: "Safari · iPhone 15",   location: "Lahore, PK",  time: "2h ago", current: false },
            ].map(({ device, location, time, current }) => (
              <div key={device} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl px-4 py-3 border"
                style={{ background: "#252320", borderColor: "#2a2825" }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <i className={`ti ${device.includes("iPhone") ? "ti-device-mobile" : "ti-device-laptop"} text-stone-500 text-sm flex-shrink-0`} />
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium text-stone-300">{device}</div>
                    <div className="text-[10px] text-stone-600">{location} · {time}</div>
                  </div>
                </div>
                {current
                  ? <span className="text-[9px] font-semibold text-emerald-400 bg-emerald-950 rounded-full px-2 py-0.5 self-start sm:self-auto flex-shrink-0">Current</span>
                  : <button className="text-[10px] text-red-400 hover:text-red-300 transition-colors font-medium self-start sm:self-auto flex-shrink-0">Revoke</button>
                }
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
// ─── TAB: BILLING ─────────────────────────────────────────────────────────────
function BillingTab() {
  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon="ti-credit-card"
        iconBg="linear-gradient(135deg,#e8602a,#c84a1e)"
        title="Billing & Subscription"
        subtitle="Manage your plan, usage, and payment details"
      >
        <div className="rounded-xl p-4 border relative overflow-hidden"
          style={{ background: "#1e1c1a", borderColor: "#e8602a30" }}
        >
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle,#e8602a,transparent)" }} />
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="text-[10px] text-orange-500 uppercase tracking-wider font-semibold mb-1">Current Plan</div>
              <div className="text-xl font-extrabold text-stone-100">Pro</div>
              <div className="text-[11px] text-stone-500 mt-0.5">$49 / month · Renews Aug 1, 2026</div>
            </div>
            <button className="text-xs font-semibold text-white rounded-xl px-4 py-2.5 hover:opacity-90 transition-opacity flex-shrink-0 self-start sm:self-auto"
              style={{ background: "#e8602a" }}
            >
              Upgrade Plan
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            {[
              { label: "Conversations", used: 3840, limit: 5000 },
              { label: "Leads",         used: 842,  limit: 1000 },
              { label: "Channels",      used: 5,    limit: 8    },
            ].map(({ label, used, limit }) => {
              const pct = Math.round((used / limit) * 100);
              const color = pct > 80 ? "#e8602a" : pct > 60 ? "#f59e0b" : "#34c97c";
              return (
                <div key={label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] text-stone-600">{label}</span>
                    <span className="text-[9px] text-stone-500">{used}/{limit}</span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: "#333" }}>
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Divider />

        <div>
          <div className="text-[10px] text-stone-500 uppercase tracking-wider mb-2">Payment Method</div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl px-4 py-3 border"
            style={{ background: "#252320", borderColor: "#2a2825" }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "#1a1815", border: "1px solid #333" }}
              >
                <i className="ti ti-credit-card text-orange-500 text-sm" />
              </div>
              <div className="min-w-0">
                <div className="text-[12px] font-medium text-stone-200">Visa ending in 4242</div>
                <div className="text-[10px] text-stone-600">Expires 09/28</div>
              </div>
            </div>
            <button className="text-[10px] text-orange-500 font-medium hover:text-orange-400 transition-colors self-start sm:self-auto flex-shrink-0">Update</button>
          </div>
        </div>

        <Divider />
        <div>
          <div className="text-[10px] text-stone-500 uppercase tracking-wider mb-2">Recent Invoices</div>
          <div className="flex flex-col gap-2">
            {[
              { date: "Jul 1, 2026", amount: "$49.00", status: "Paid" },
              { date: "Jun 1, 2026", amount: "$49.00", status: "Paid" },
              { date: "May 1, 2026", amount: "$49.00", status: "Paid" },
            ].map(({ date, amount, status }) => (
              <div key={date} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl px-4 py-3 border"
                style={{ background: "#252320", borderColor: "#2a2825" }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <i className="ti ti-file-invoice text-stone-600 text-sm flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium text-stone-300">{date}</div>
                    <div className="text-[10px] text-stone-600">{amount}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
                  <span className="text-[9px] font-semibold text-emerald-400 bg-emerald-950 rounded-full px-2 py-0.5">{status}</span>
                  <button className="text-[10px] text-stone-600 hover:text-stone-300 transition-colors">
                    <i className="ti ti-download text-xs" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
// ─── MAIN SETTINGS CONTENT ────────────────────────────────────────────────────
function SettingsContent() {
  const [activeTab, setActiveTab] = useState("business");
  const [saved, setSaved] = useState(false);

  // State for each section
  const [business, setBusiness] = useState({
    name: "Fitness Zone", email: "sajid.ali@example.com",
    phone: "", website: "", description: "", industry: "Fitness & Wellness", timezone: "UTC+5 (PKT)",
  });
  const [ecosystem, setEcosystem] = useState({
    nigellaFit: false, nigellaERP: false, webhookUrl: "", apiToken: "", syncTrigger: "On Lead Qualified",
  });
  const [ai, setAi] = useState({
    openAiKey: "sk-…", model: "Nigella VOX v1.3 (default)", language: "English",
    systemPrompt: "", autoEscalate: true, leadScoring: true, sentimentAnalysis: false, multiLingual: false,
  });
  const [notifications, setNotifications] = useState({
    newLead: true, escalation: true, weeklyReport: false, channelDown: true, lowScore: false,
    notifEmail: "", frequency: "Instant",
  });
  const [security, setSecurity] = useState({
    currentPw: "", newPw: "", twoFA: false, sessionAlert: true, ipWhitelist: false,
  });

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const tabContent = {
    business:      <BusinessTab      data={business}      setData={setBusiness} />,
    ecosystem:     <EcosystemTab     data={ecosystem}     setData={setEcosystem} />,
    ai:            <AITab            data={ai}            setData={setAi} />,
    notifications: <NotificationsTab data={notifications} setData={setNotifications} />,
    security:      <SecurityTab      data={security}      setData={setSecurity} />,
    billing:       <BillingTab />,
  };

  return (
    <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
      {/* Left settings nav — desktop only */}
      <div className="hidden lg:flex flex-col flex-shrink-0 border-r overflow-y-auto py-4 px-2"
        style={{ width: 180, background: "#161412", borderColor: "#2a2825" }}
      >
        <div className="px-3 mb-3">
          <div className="text-[9px] text-stone-600 uppercase tracking-widest">Settings</div>
        </div>
        {settingsTabs.map(({ id, icon, label }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 text-left transition-all w-full ${
              activeTab === id
                ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                : "text-stone-400 hover:text-stone-200 hover:bg-white/5"
            }`}
          >
            <i className={`ti ${icon} text-sm flex-shrink-0`} />
            <span className="text-xs font-medium">{label}</span>
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 lg:px-6 py-4 border-b flex-shrink-0"
          style={{ background: "#111010", borderColor: "#2a2825" }}
        >
          <div>
            <h1 className="text-base font-extrabold text-stone-100">Settings</h1>
            <p className="text-[10px] text-stone-500 mt-0.5">Business profile &amp; ecosystem integration</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {saved && (
              <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-medium">
                <i className="ti ti-circle-check text-sm" />
                Saved successfully
              </span>
            )}
            <button onClick={handleSave}
              className="flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold text-white hover:opacity-90 transition-all w-full sm:w-auto"
              style={{ background: "#e8602a" }}
            >
              <i className="ti ti-device-floppy text-sm" />
              Save Changes
            </button>
          </div>
        </div>

        {/* Mobile horizontal tab bar — replaces left nav on small screens */}
        <div className="flex lg:hidden items-center gap-2 px-4 py-3 border-b overflow-x-auto flex-shrink-0"
          style={{ background: "#161412", borderColor: "#2a2825" }}>
          {settingsTabs.map(({ id, icon, label }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium transition-all flex-shrink-0 ${
                activeTab === id
                  ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                  : "text-stone-400 border border-transparent"
              }`}>
              <i className={`ti ${icon} text-sm`} />
              {label}
            </button>
          ))}
        </div>

        {/* Scrollable tab content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-5 pb-24 lg:pb-5" style={{ background: "#111010" }}>
          {tabContent[activeTab]}
        </div>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function Settings({ onLogout }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex h-screen w-screen overflow-hidden" style={{ background: "#111010" }}>
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar onLogout={onLogout} onMenuOpen={() => setDrawerOpen(true)} />
        <SettingsContent />
      </div>

      {/* Mobile bottom nav */}
      <MobileBottomNav />
    </div>
  );
}